import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, LogOut, Trash2 } from 'lucide-react';
import apiClient from '../services/apiClient';
import authService from '../services/AuthService';
import Spinner from './Spinner';
import ErrorMessage from './ErrorMessage';
import ThemeSelector from './ThemeSelector';
import { getLanguage, setLanguage, SUPPORTED_LANGUAGES, t } from '../i18n';
import './UserPage.css';

function getNickname(user = {}) {
  return user.nickname || user.name || user.userId || '';
}

function validateNickname(value) {
  const nickname = value.trim();
  const length = Array.from(nickname).length;

  if (length === 0) return t('accountActivity.user.nicknameRequired');
  if (length < 2) return t('accountActivity.user.nicknameShort');
  if (length > 10) return t('accountActivity.user.nicknameLong');
  return '';
}

function UserPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [isSavingNickname, setIsSavingNickname] = useState(false);
  const [isPasswordFormOpen, setIsPasswordFormOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteAccountError, setDeleteAccountError] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [interfaceLanguage, setInterfaceLanguage] = useState(getLanguage);

  useEffect(() => {
    const syncLanguage = () => setInterfaceLanguage(getLanguage());
    window.addEventListener('appLanguageChanged', syncLanguage);
    return () => window.removeEventListener('appLanguageChanged', syncLanguage);
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await apiClient.getCurrentUserSummary();
        setProfile(data);
        setNicknameDraft(getNickname(data?.user));
      } catch (err) {
        console.error('Failed to load user profile:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  const handleEditNickname = () => {
    setNicknameDraft(getNickname(profile?.user));
    setNicknameError('');
    setIsEditingNickname(true);
  };

  const handleCancelNickname = () => {
    setNicknameDraft(getNickname(profile?.user));
    setNicknameError('');
    setIsEditingNickname(false);
  };

  const handleSaveNickname = async () => {
    const validationError = validateNickname(nicknameDraft);
    if (validationError) {
      setNicknameError(validationError);
      return;
    }

    setIsSavingNickname(true);
    setNicknameError('');

    try {
      const updatedUser = await apiClient.updateCurrentUser({ nickname: nicknameDraft.trim() });
      setProfile((current) => ({
        ...current,
        user: {
          ...(current?.user || {}),
          ...updatedUser,
        },
      }));
      setNicknameDraft(updatedUser.nickname || updatedUser.name || nicknameDraft.trim());

      const session = authService.getSession?.();
      if (session) {
        const nickname = updatedUser.nickname || updatedUser.name || nicknameDraft.trim();
        authService.setSession({ ...session, nickname, name: nickname });
        window.dispatchEvent(new Event('authStateChanged'));
      }

      setIsEditingNickname(false);
    } catch (err) {
      setNicknameError(err.message || t('accountActivity.user.nicknameFailed'));
    } finally {
      setIsSavingNickname(false);
    }
  };

  const handlePasswordField = (field, value) => {
    setPasswordForm((current) => ({ ...current, [field]: value }));
    setPasswordError('');
    setPasswordMessage('');
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (passwordForm.newPassword.length < 8) {
      setPasswordError(t('accountActivity.user.passwordShort'));
      return;
    }
    if (!/[A-Za-z]/.test(passwordForm.newPassword) || !/\d/.test(passwordForm.newPassword)) {
      setPasswordError(t('accountActivity.user.passwordFormat'));
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('accountActivity.user.passwordMismatch'));
      return;
    }

    setIsChangingPassword(true);
    setPasswordError('');
    setPasswordMessage('');
    try {
      await apiClient.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordMessage(t('accountActivity.user.passwordChanged'));
      setIsPasswordFormOpen(false);
    } catch (err) {
      setPasswordError(err.message || t('accountActivity.user.passwordFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async (event) => {
    event.preventDefault();
    if (!deletePassword) {
      setDeleteAccountError(t('accountActivity.user.passwordRequired'));
      return;
    }

    const confirmed = window.confirm(
      t('accountActivity.user.deleteConfirmation')
    );
    if (!confirmed) return;

    setIsDeletingAccount(true);
    setDeleteAccountError('');
    try {
      await apiClient.deleteAccount({ password: deletePassword });
      await authService.logout();
      window.dispatchEvent(new Event('authStateChanged'));
      navigate('/login');
    } catch (err) {
      setDeleteAccountError(err.message || t('accountActivity.user.deleteFailed'));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const pageHeader = (
    <header className="user-page-header">
      <button type="button" className="btn-back-inline" onClick={handleBack} aria-label={t('accountActivity.backHome')} title={t('accountActivity.backHome')}>
        <ArrowLeft size={24} strokeWidth={2.4} aria-hidden="true" />
      </button>
      <div className="user-page-title-block">
        <h1 className="page-title-fx page-title-user">{t('accountActivity.user.title')}</h1>
        <p>{t('accountActivity.user.subtitle')}</p>
      </div>
      <button type="button" className="user-page-logout" onClick={handleLogout}>
        <LogOut size={17} aria-hidden="true" />
        <span>{t('accountActivity.user.logout')}</span>
      </button>
    </header>
  );

  if (isLoading) {
    return (
      <div className="user-page">
        {pageHeader}
        <Spinner size="large" message={t('accountActivity.user.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-page">
        {pageHeader}
        <ErrorMessage
          error={error}
          onRetry={() => window.location.reload()}
          type={error?.isNetworkError ? 'network' : 'api'}
        />
      </div>
    );
  }

  const user = profile?.user || {};
  const summary = profile?.summary || {};
  const exp = Math.max(0, Math.floor(Number(summary.exp ?? summary.totalScoreGained) || 0));
  const levelStep = Math.max(1, Math.floor(Number(summary.levelExpStep) || 5000));
  const level = Math.floor(exp / levelStep) + 1;
  const levelProgress = exp % levelStep;
  const nickname = getNickname(user);
  const membershipStatus = user.membershipStatus || 'membership';
  const trialEndDate = user.trialEndsAt
    ? new Date(user.trialEndsAt).toLocaleDateString(getLanguage() === 'ko' ? 'ko-KR' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '';
  const membershipMessage = t(`membership.account.${membershipStatus}`, undefined, { date: trialEndDate });
  const accountCard = (
    <div className="user-profile-card user-account-card">
      <div className="user-account-heading">
        <span className="user-stat-label">{t('accountActivity.user.account')}</span>
        <span className={`user-membership-badge is-${membershipStatus}`}>
          {t(`membership.labels.${membershipStatus}`)}
        </span>
      </div>
      <div className={`user-membership-message is-${membershipStatus}`}>{membershipMessage}</div>
      <div className="user-account-details">
        <span>{t('accountActivity.user.email')}: {user.email || '-'}</span>
        <span>{t('accountActivity.user.emailVerification')}: {t(`accountActivity.user.${user.emailVerifiedAt ? 'verified' : 'pending'}`)}</span>
      </div>
    </div>
  );
  const deleteAccountCard = (
      <div className="user-danger-zone">
        <div>
          <strong>{t('accountActivity.user.deleteTitle')}</strong>
          <p>{t('accountActivity.user.deleteDescription')}</p>
        </div>
        {isDeleteAccountOpen ? (
          <form className="user-delete-account-form" onSubmit={handleDeleteAccount}>
            <label htmlFor="delete-account-password">{t('accountActivity.user.currentPassword')}</label>
            <input
              id="delete-account-password"
              type="password"
              placeholder={t('accountActivity.user.currentPassword')}
              value={deletePassword}
              onChange={(event) => {
                setDeletePassword(event.target.value);
                setDeleteAccountError('');
              }}
              disabled={isDeletingAccount}
              autoComplete="current-password"
              required
            />
            <div className="user-danger-actions">
              <button type="submit" disabled={isDeletingAccount}>
                {t(`accountActivity.user.${isDeletingAccount ? 'deleting' : 'deleteAction'}`)}
              </button>
              <button
                type="button"
                disabled={isDeletingAccount}
                onClick={() => {
                  setIsDeleteAccountOpen(false);
                  setDeletePassword('');
                  setDeleteAccountError('');
                }}
              >
                {t('accountActivity.cancel')}
              </button>
            </div>
            {deleteAccountError && <span className="user-nickname-error">{deleteAccountError}</span>}
          </form>
        ) : (
          <button
            type="button"
            className="user-delete-account-button"
            onClick={() => setIsDeleteAccountOpen(true)}
          >
            <Trash2 size={16} aria-hidden="true" />
            <span>{t('accountActivity.user.deleteAction')}</span>
          </button>
        )}
      </div>
  );
  const changePasswordCard = (
    <div className={`user-profile-card user-password-card${isPasswordFormOpen ? ' is-open' : ''}`}>
      {!isPasswordFormOpen ? (
        <button
          type="button"
          className="user-password-open"
          onClick={() => {
            setPasswordError('');
            setPasswordMessage('');
            setIsPasswordFormOpen(true);
          }}
        >
          <KeyRound size={17} aria-hidden="true" />
          <span>{t('accountActivity.user.changePassword')}</span>
        </button>
      ) : (
        <form className="user-password-form" onSubmit={handleChangePassword}>
          <span className="user-stat-label">{t('accountActivity.user.changePassword')}</span>
          <p className="user-form-help" id="password-guidance">{t('accountActivity.user.passwordGuidance')}</p>
          <div className="user-password-grid">
            <label htmlFor="account-current-password">{t('accountActivity.user.currentPassword')}</label>
            <input
              id="account-current-password"
              type="password"
              placeholder={t('accountActivity.user.currentPassword')}
              value={passwordForm.currentPassword}
              onChange={(event) => handlePasswordField('currentPassword', event.target.value)}
              disabled={isChangingPassword}
              autoComplete="current-password"
              required
            />
            <label htmlFor="account-new-password">{t('accountActivity.user.newPassword')}</label>
            <input
              id="account-new-password"
              type="password"
              placeholder={t('accountActivity.user.newPassword')}
              aria-describedby="password-guidance"
              value={passwordForm.newPassword}
              onChange={(event) => handlePasswordField('newPassword', event.target.value)}
              disabled={isChangingPassword}
              autoComplete="new-password"
              required
            />
            <label htmlFor="account-confirm-password">{t('accountActivity.user.confirmPassword')}</label>
            <input
              id="account-confirm-password"
              type="password"
              placeholder={t('accountActivity.user.confirmPassword')}
              value={passwordForm.confirmPassword}
              onChange={(event) => handlePasswordField('confirmPassword', event.target.value)}
              disabled={isChangingPassword}
              autoComplete="new-password"
              required
            />
          </div>
          {passwordError && <span className="user-nickname-error">{passwordError}</span>}
          <div className="user-nickname-actions">
            <button type="submit" className="user-save-button" disabled={isChangingPassword}>
              {t(`accountActivity.user.${isChangingPassword ? 'changing' : 'confirm'}`)}
            </button>
            <button type="button" disabled={isChangingPassword} onClick={() => {
              setIsPasswordFormOpen(false);
              setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setPasswordError('');
            }}>{t('accountActivity.cancel')}</button>
          </div>
        </form>
      )}
      {passwordMessage && <span className="user-password-message">{passwordMessage}</span>}
    </div>
  );

  return (
    <div className="user-page">
      {pageHeader}

      <section className="user-page-section user-profile-overview" aria-label={t('accountActivity.user.profileOverview')}>
        <div className="user-profile-card">
          <span className="user-stat-label">{t('accountActivity.user.nickname')}</span>
          {isEditingNickname ? (
            <div className="user-nickname-editor">
              <input
                aria-label={t('accountActivity.user.nickname')}
                value={nicknameDraft}
                onChange={(event) => {
                  setNicknameDraft(event.target.value);
                  if (nicknameError) setNicknameError('');
                }}
                maxLength={20}
                disabled={isSavingNickname}
              />
              {nicknameError && <span className="user-nickname-error">{nicknameError}</span>}
              <div className="user-nickname-actions">
                <button type="button" className="user-save-button" onClick={handleSaveNickname} disabled={isSavingNickname}>
                  {t(`accountActivity.${isSavingNickname ? 'saving' : 'save'}`)}
                </button>
                <button type="button" onClick={handleCancelNickname} disabled={isSavingNickname}>
                  {t('accountActivity.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="user-nickname-display">
              <span className="user-profile-name">{nickname || '-'}</span>
              <button type="button" onClick={handleEditNickname}>
                {t('accountActivity.edit')}
              </button>
            </div>
          )}
        </div>
        <div className="user-level-summary">
          <div><span>{t('accountActivity.user.level')}</span><strong>Lv. {level}</strong></div>
          <progress aria-label={t('accountActivity.user.levelProgress')} max={levelStep} value={levelProgress} />
          <p>{t('accountActivity.user.nextLevel', undefined, { current: levelProgress.toLocaleString(), total: levelStep.toLocaleString() })}</p>
        </div>
      </section>

      <section className="user-page-section" aria-labelledby="user-settings-title">
        <h2 className="user-page-title" id="user-settings-title">{t('accountActivity.user.settings')}</h2>
        <div className="user-setting-row">
          <div>
            <label htmlFor="user-display-language">{t('accountActivity.user.displayLanguage')}</label>
            <p id="user-display-language-help">{t('accountActivity.user.languageHelp')}</p>
          </div>
          <select id="user-display-language" aria-describedby="user-display-language-help" value={interfaceLanguage} onChange={(event) => setInterfaceLanguage(setLanguage(event.target.value))}>
            {SUPPORTED_LANGUAGES.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}
          </select>
        </div>
        <ThemeSelector language={interfaceLanguage} />
      </section>

      <section className="user-page-section" aria-labelledby="user-security-title">
        <h2 className="user-page-title" id="user-security-title">{t('accountActivity.user.security')}</h2>
        {accountCard}
        {changePasswordCard}
      </section>

      <section className="user-page-section user-danger-section" aria-labelledby="user-danger-title">
        <h2 className="user-page-title" id="user-danger-title">{t('accountActivity.user.danger')}</h2>
        {deleteAccountCard}
      </section>
    </div>
  );
}

export default UserPage;
